import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Modal } from '../components/common';
import { LineSettings, GoogleSheetsSettings } from '../components/settings';
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import {
  db,
  getAllInspections,
} from '../db';
import {
  downloadCsvInspectionLogs,
  downloadJsonBackup,
  restoreFullDatabaseBackup,
} from '../services/sync';

export const SettingsView: React.FC = () => {
  // Database Diagnostics state
  const [inspectionsCount, setInspectionsCount] = useState<number>(0);
  const [pendingQueueCount, setPendingQueueCount] = useState<number>(0);
  const [isClearingQueue, setIsClearingQueue] = useState<boolean>(false);
  const [queueClearedMsg, setQueueClearedMsg] = useState<string>('');
  const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);
  const [isExportingJson, setIsExportingJson] = useState<boolean>(false);
  const [isRestoringJson, setIsRestoringJson] = useState<boolean>(false);
  const [restoreFeedback, setRestoreFeedback] = useState<string>('');

  // Reset Modal state
  const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);

  // Hidden file input for JSON restore
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load diagnostic counts
  const loadDiagnostics = async () => {
    try {
      const insCount = await db.inspections.count();
      setInspectionsCount(insCount);

      const qCount = await db.syncQueue
        .where('status')
        .anyOf('PENDING', 'FAILED', 'SYNCING' as any)
        .count();
      setPendingQueueCount(qCount);
    } catch (err) {
      console.error('[SettingsView] Error loading diagnostics:', err);
    }
  };

  useEffect(() => {
    loadDiagnostics();
  }, []);

  // Clear Sync Queue
  const handleClearQueue = async () => {
    setIsClearingQueue(true);
    try {
      await db.syncQueue.clear();
      setPendingQueueCount(0);
      setQueueClearedMsg('ล้างรายการในคิวซิงค์เรียบร้อยแล้ว');
      setTimeout(() => setQueueClearedMsg(''), 3000);
    } catch (err: any) {
      alert(`ไม่สามารถล้างคิวได้: ${err.message}`);
    } finally {
      setIsClearingQueue(false);
    }
  };

  // Export CSV Logs with UTF-8 BOM for Thai language Excel support
  const handleExportCsv = async () => {
    setIsExportingCsv(true);
    try {
      const inspections = await getAllInspections();
      if (inspections.length === 0) {
        alert('ยังไม่มีประวัติการตรวจเช็กในระบบ');
        return;
      }
      await downloadCsvInspectionLogs(inspections);
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาดในการสร้างไฟล์ CSV: ${err.message}`);
    } finally {
      setIsExportingCsv(false);
    }
  };

  // Export Full JSON Backup
  const handleExportJson = async () => {
    setIsExportingJson(true);
    try {
      await downloadJsonBackup();
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาดในการสำรองข้อมูล JSON: ${err.message}`);
    } finally {
      setIsExportingJson(false);
    }
  };

  // Import / Restore JSON Backup
  const handleRestoreFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsRestoringJson(true);
    setRestoreFeedback('');

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const content = event.target?.result as string;
          const result = await restoreFullDatabaseBackup(content);
          setRestoreFeedback(
            `กู้คืนข้อมูลสำเร็จ: เครื่องจักร ${result.importedCounts.machines} รายการ, แบบตรวจ ${result.importedCounts.templates} รายการ, ผลตรวจ ${result.importedCounts.inspections} รายการ`
          );
          await loadDiagnostics();
          setTimeout(() => setRestoreFeedback(''), 6000);
        } catch (parseErr: any) {
          alert(`ไฟล์สำรองข้อมูลไม่ถูกต้อง: ${parseErr.message}`);
        } finally {
          setIsRestoringJson(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsText(file);
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาดในการอ่านไฟล์: ${err.message}`);
      setIsRestoringJson(false);
    }
  };

  // Reset Database
  const handleResetDatabase = async () => {
    try {
      await db.delete();
      window.location.reload();
    } catch (err: any) {
      alert(`เกิดข้อผิดพลาดในการรีเซ็ต: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* 1. Google Sheets Cloud Sync Settings (Componentized) */}
      <GoogleSheetsSettings />

      {/* 2. LINE Anomaly & Webhook Alerting Settings */}
      <LineSettings />

      {/* 3. Offline Data Storage & Diagnostics */}
      <Card
        title={
          <div className="flex items-center gap-2.5">
            <Database className="w-5 h-5 text-amber-600" />
            <span className="text-slate-900 font-bold">
              ฐานข้อมูลออฟไลน์ในเครื่อง (Offline Storage & Data Export)
            </span>
          </div>
        }
        subtitle="จัดการข้อมูลที่จัดเก็บบน IndexedDB ของอุปกรณ์ และสำรองข้อมูลเป็นไฟล์ JSON หรือ CSV (รองรับภาษาไทยใน Excel)"
      >
        <div className="space-y-4">
          {/* Metrics summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-500 font-medium">ประวัติการตรวจเช็กในเครื่อง</span>
                <p className="text-lg font-bold text-slate-900">{inspectionsCount} รายการ</p>
              </div>
              <HardDrive className="w-6 h-6 text-slate-400" />
            </div>

            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-500 font-medium">รายการรอซิงค์ (Pending Sync)</span>
                <p className="text-lg font-bold text-amber-700">{pendingQueueCount} รายการ</p>
              </div>
              <RefreshCw className="w-6 h-6 text-amber-600" />
            </div>
          </div>

          {queueClearedMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-800 font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{queueClearedMsg}</span>
            </div>
          )}

          {restoreFeedback && (
            <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-800 font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{restoreFeedback}</span>
            </div>
          )}

          {/* Export & Diagnostics Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleExportCsv}
              isLoading={isExportingCsv}
              leftIcon={<FileSpreadsheet className="w-4 h-4 text-emerald-600" />}
            >
              ส่งออกประวัติเป็น CSV (Excel Thai UTF-8 BOM)
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleExportJson}
              isLoading={isExportingJson}
              leftIcon={<Download className="w-4 h-4 text-blue-600" />}
            >
              สำรองข้อมูลทั้งหมด (Export JSON Backup)
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              isLoading={isRestoringJson}
              leftIcon={<Upload className="w-4 h-4 text-purple-600" />}
            >
              กู้คืนข้อมูลสำรอง (Restore JSON)
            </Button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleRestoreFileSelected}
              accept=".json,application/json"
              className="hidden"
            />

            <Button
              type="button"
              variant="secondary"
              onClick={handleClearQueue}
              isLoading={isClearingQueue}
              leftIcon={<RefreshCw className="w-4 h-4 text-amber-600" />}
            >
              ล้างคิวซิงค์ (Clear Sync Queue)
            </Button>

            <Button
              type="button"
              variant="danger"
              onClick={() => setShowResetConfirm(true)}
              leftIcon={<Trash2 className="w-4 h-4" />}
              className="ml-auto"
            >
              รีเซ็ตฐานข้อมูล (Reset DB)
            </Button>
          </div>
        </div>
      </Card>

      {/* Reset Confirmation Modal */}
      <Modal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="ยืนยันการรีเซ็ตฐานข้อมูลเครื่องจักร"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowResetConfirm(false)}>
              ยกเลิก
            </Button>
            <Button variant="danger" onClick={handleResetDatabase}>
              ยืนยันการรีเซ็ตทั้งหมด
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          <p className="font-semibold text-rose-600 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
            คำเตือน: การกระทำนี้ไม่สามารถย้อนกลับได้
          </p>
          <p>
            ข้อมูลประวัติการตรวจเช็กทั้งหมด, รายการคิวซิงค์, โปรไฟล์เครื่องจักร และเทมเพลตที่แก้ไขจะถูกลบออกจากอุปกรณ์นี้ทั้งหมด และรีโหลดกลับสู่ค่าตั้งต้น
          </p>
        </div>
      </Modal>
    </div>
  );
};
